import { formatDate } from './dateUtils';

/** Human-readable labels for StaffUser fields audited on direct edit. */
export const PROFILE_AUDIT_FIELD_LABELS = {
  first_name: 'First Name',
  last_name: 'Last Name',
  alias: 'Alias',
  work_email: 'Work Email',
  work_phone_number: 'Work Phone',
  did_number: 'Direct line',
  extension_number: 'Extension',
  mom_designation: 'MOM Designation',
  designation: 'Designation',
  department: 'Department',
  reporting_officer: 'Reporting Officer',
  employment_type: 'Employment Type',
  employment_status: 'Employment Status',
  employment_classification: 'Classification',
  attendance_tracking_mode: 'Attendance Tracking',
  work_schedule_template: 'Work Schedule',
  self_approve_leave: 'Self-Approve Leave',
  employment_start_date: 'Start Date',
  employment_end_date: 'End Date',
  security_bond_expiry_date: 'Security Bond Expiry',
  probation_end_date: 'Probation End Date',
  probation_extended: 'Probation Extended',
  probation_period_months: 'Probation Period (months)',
};

const DATE_FIELD_KEYS = new Set([
  'employment_start_date',
  'employment_end_date',
  'security_bond_expiry_date',
  'probation_end_date',
]);

const SYSTEM_ACTIVITY_ACTIONS = new Set([
  'LOGIN',
  'LOGOUT',
  'READ',
  'ACCESS_DENIED',
]);

export const PROFILE_AUDIT_ACTION_COLORS = {
  UPDATE: 'bg-amber-100 text-amber-800',
  CREATE: 'bg-emerald-100 text-emerald-800',
  DELETE: 'bg-rose-100 text-rose-800',
  LOGIN: 'bg-slate-100 text-slate-700',
  LOGOUT: 'bg-slate-100 text-slate-700',
  PASSWORD_CHANGE: 'bg-purple-100 text-purple-800',
  PASSWORD_CHANGE_FORCED: 'bg-purple-100 text-purple-800',
  PASSWORD_RESET_COMPLETE: 'bg-purple-100 text-purple-800',
};

const FIELD_CHANGE_PATTERN = /^(\S+) changed from '(.*)' to '(.*)' by (.+)$/s;
/** Newer writes: ``Full Name (S0102)``. Legacy: bare staff_id. */
const NAMED_ACTOR_PATTERN = /^(.+?)\s+\((\S+)\)$/;

/**
 * Parse actor token from audit_details into staff_id + optional name hint.
 *
 * @param {string} rawActor
 * @returns {{ actorId: string|null, actorName: string|null }}
 */
export const parseAuditActorToken = (rawActor) => {
  const trimmed = (rawActor || '').trim();
  if (!trimmed) return { actorId: null, actorName: null };

  const named = NAMED_ACTOR_PATTERN.exec(trimmed);
  if (named) {
    return {
      actorId: named[2],
      actorName: named[1].trim() || null,
    };
  }

  if (trimmed.toLowerCase() === 'system') {
    return { actorId: 'system', actorName: 'System' };
  }

  return { actorId: trimmed, actorName: null };
};

/**
 * Parse a StaffUser audit log entry into a structured shape for display.
 *
 * Prefers API-enriched ``actor_name`` / ``actor_staff_id`` when present
 * (resolved server-side for historical bare-id rows).
 *
 * @param {{
 *   audit_action_type?: string,
 *   audit_details?: string,
 *   actor_name?: string|null,
 *   actor_staff_id?: string|null,
 * }} entry
 * @returns {{
 *   kind: string,
 *   fieldKey?: string,
 *   fromValue?: string,
 *   toValue?: string,
 *   actorId?: string|null,
 *   actorName?: string|null,
 *   summary?: string,
 * }}
 */
export const parseProfileAuditEntry = (entry) => {
  const details = entry?.audit_details || '';
  const match = FIELD_CHANGE_PATTERN.exec(details);

  if (match) {
    const fromDetails = parseAuditActorToken(match[4]);
    const actorId = entry?.actor_staff_id || fromDetails.actorId;
    const actorName = entry?.actor_name || fromDetails.actorName || null;
    return {
      kind: 'field_change',
      fieldKey: match[1],
      fromValue: match[2],
      toValue: match[3],
      actorId,
      actorName,
    };
  }

  if (entry?.audit_action_type === 'LOGIN') {
    return { kind: 'login', summary: details || 'Logged in to the portal' };
  }

  if (entry?.audit_action_type === 'LOGOUT') {
    return { kind: 'logout', summary: details || 'Logged out of the portal' };
  }

  return {
    kind: 'other',
    summary: details || entry?.audit_action_type || '—',
  };
};

/**
 * Format a raw audit value for HR-readable display.
 *
 * @param {string} [fieldKey]
 * @param {string|null|undefined} rawValue
 * @returns {{ display: string, isEmpty: boolean }}
 */
export const formatProfileAuditValue = (fieldKey, rawValue) => {
  if (rawValue == null || rawValue === '' || rawValue === 'None' || rawValue === 'null') {
    return { display: 'Not set', isEmpty: true };
  }

  if (rawValue === 'True') return { display: 'Yes', isEmpty: false };
  if (rawValue === 'False') return { display: 'No', isEmpty: false };

  if (fieldKey && DATE_FIELD_KEYS.has(fieldKey)) {
    const isoDate = /^\d{4}-\d{2}-\d{2}$/.test(rawValue);
    if (isoDate) {
      return { display: formatDate(rawValue), isEmpty: false };
    }
  }

  const employmentTypeLabels = { full_time: 'Full-Time', part_time: 'Part-Time' };
  if (fieldKey === 'employment_type' && employmentTypeLabels[rawValue]) {
    return { display: employmentTypeLabels[rawValue], isEmpty: false };
  }

  const statusLabels = {
    active: 'Active',
    inactive: 'Inactive',
    probation: 'On Probation',
    notice: 'Serving Notice',
  };
  if (fieldKey === 'employment_status' && statusLabels[rawValue]) {
    return { display: statusLabels[rawValue], isEmpty: false };
  }

  const trackingLabels = {
    standard: 'Standard',
    flexible: 'Flexible',
    exempt: 'Exempt',
  };
  if (fieldKey === 'attendance_tracking_mode' && trackingLabels[rawValue]) {
    return { display: trackingLabels[rawValue], isEmpty: false };
  }

  return { display: rawValue, isEmpty: false };
};

/** @param {{ audit_action_type?: string, audit_details?: string }} entry */
export const isProfileFieldEdit = (entry) => {
  const parsed = parseProfileAuditEntry(entry);
  if (parsed.kind === 'field_change') return true;
  if (SYSTEM_ACTIVITY_ACTIONS.has(entry?.audit_action_type)) return false;
  return entry?.audit_action_type === 'UPDATE';
};

/**
 * @param {Array<object>} entries
 * @param {'edits'|'all'} mode
 */
export const filterProfileAuditLog = (entries, mode) => {
  if (mode === 'all') return entries;
  return entries.filter(isProfileFieldEdit);
};

/** @param {string} fieldKey */
export const getProfileAuditFieldLabel = (fieldKey) => {
  if (!fieldKey) return '—';
  return PROFILE_AUDIT_FIELD_LABELS[fieldKey] || fieldKey.replace(/_/g, ' ');
};

/** @param {string} actionType */
export const getProfileAuditActionLabel = (actionType) => {
  if (!actionType) return 'Event';
  if (actionType === 'UPDATE') return 'Edit';
  if (actionType === 'LOGIN') return 'Login';
  if (actionType === 'LOGOUT') return 'Logout';
  return actionType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};
